import { BREAKPOINTS, COLORS, ComponentL, IconButton, SystemIcons } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocale, selectMarket, setMarket } from '../store/slices/globalConfigSlice';
import { marketList } from '../model/constants/marketContants';
import { getPathWithoutMarket } from '../model/constants/marketUrl';
import { useNavigate } from 'react-router-dom';
import { useMediaMatch } from "rooks";


const Container = styled.div`
  background: ${COLORS.neutral_700};
  display: flex;

  height: 36px;

  align-items: center;
  position: relative;
`;

const MainButton = styled.div`
  /* mirrors laerdal.com .c-language-picker__select; 8px padding + margin aligns the flag with the global nav logo/menu icon (28/40/72px) */
  margin: 2px 20px;
  padding: 4px 8px;

  ${BREAKPOINTS.MEDIUM}{
    margin: 2px 32px;
  }
  ${BREAKPOINTS.LARGE}{
    margin: 2px 64px;
    /* Global nav centers its content in a 1744px box (1600px + 2*72px) relative to the window width on ultrawide screens */
    margin-left: max(64px, calc((100vw - 1744px) / 2 + 64px));
  }

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  cursor: pointer;
  color: ${COLORS.white};
  font-size: 16px;
  line-height: 20px;
  border-radius: 4px;

  &:hover {
    background: ${COLORS.primary_20};
    color: ${COLORS.primary_700};
  }

  &:active {
    background: ${COLORS.primary_20};
    color: ${COLORS.primary_800};
  }
`;

const DropdownContainer = styled.div<{ $isOpen: boolean }>`
  /* horizontal padding mirrors MainButton margins (+8px button padding) so panel content aligns with the collapsed button at every breakpoint */
  box-sizing: border-box;
  width: 100%;
  padding: 16px 28px;
  flex-direction: column;

  ${BREAKPOINTS.MEDIUM}{
    flex-direction: row;
    flex-wrap: wrap;
    padding: 40px 40px 48px;
    column-gap: 20px;
    justify-content: flex-start;
  }
  ${BREAKPOINTS.LARGE}{
    padding: 40px 72px 48px;
    padding-left: max(72px, calc((100vw - 1744px) / 2 + 72px));
  }

  position: absolute;
  top: 100%;
  left: 0;
  background: ${COLORS.black};
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  z-index: 100000;

  color: ${COLORS.white};
`;

const H3 = styled.div`
  flex-direction: column;
  display: flex;
  max-width: 261px;
  flex-grow: 1;
  font-size: 40px;
  line-height: 1.4;
  margin-bottom: 24px;
`;

const Line = styled.div`
  width: 50px;
  height: 1px;
  background: ${COLORS.white};
  /* 60px below menu top like laerdal.com; the in-flow 48px close button takes up the rest */
  margin-top: 12px;
  margin-bottom: 32px;
`;

const Column = styled.div`
  flex-direction: column;
  display: flex;
  max-width: 261px;
  flex-grow: 1;
`;

const HeaderColumn = styled(Column)`
  /* full-width row above the country columns on tablet, inline first column on desktop — like laerdal.com */
  ${BREAKPOINTS.MEDIUM}{
    flex-basis: 100%;
    max-width: none;

    ${H3} {
      max-width: none;
    }
  }
  ${BREAKPOINTS.LARGE}{
    flex-basis: auto;
    max-width: 261px;

    ${H3} {
      max-width: 261px;
    }
  }
`;

const GroupHeading = styled(ComponentL)`
  line-height: 1.4;
  margin-bottom: 10px;

  &:not(:first-child) {
    margin-top: 48px;
  }
`;

const Country = styled.div<{ $selected?: boolean }>`

  padding: 8px;
  justify-content: space-between;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  cursor: ${props => props.$selected ? 'default' : 'pointer'};
  /* selected market is grayed out and inert like on laerdal.com */
  color: ${props => props.$selected ? COLORS.neutral_300 : COLORS.white};
  font-size: 16px;
  line-height: 20px;

  &:hover {
    ${props => !props.$selected && `
      border-radius: 8px;
      background: ${COLORS.primary_20};
      color: ${COLORS.primary_700};

      div {
        color: ${COLORS.primary_700};
      }
    `}
  }
`;
const CountryLabel = styled.div`
  justify-content: start;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  cursor: inherit;
  color: inherit;
`;



const LanguageSelector = () => {
  const { t } = useTranslation('translation');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));

  const currentMartket = useSelector(selectMarket);
  const currentLocale = useSelector(selectLocale);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryClick = (marketCode: string) => {
    const pathWithoutMarket = getPathWithoutMarket(location.pathname);

    dispatch(setMarket(marketCode));
    setIsOpen(false);

    navigate(`/${marketCode}${pathWithoutMarket}${location.search}${location.hash}`)
  };

  const renderCountry = (code: string) => {
    const selected = currentMartket == code;
    return (
      <Country key={code} $selected={selected} onClick={() => !selected && handleCountryClick(code)}>
        <CountryLabel>{marketList[code].icon} {marketList[code].marketName}</CountryLabel>
        {selected && <SystemIcons.CheckMark size='24px' />}
      </Country>
    );
  };

  return (
    <Container ref={containerRef}>
      <MainButton onClick={() => setIsOpen(!isOpen)}>
        {marketList[currentMartket]?.icon}
        <div>
          {marketList[currentMartket]?.marketName}
        </div>
        <SystemIcons.ArrowDropDown size='24px' />
      </MainButton>
      <DropdownContainer $isOpen={isOpen}>
        {isMediumScreen && <HeaderColumn>
          <IconButton
            variant='secondary'
            useTransparentBackground={true}
            shape='circular'
            action={() => setIsOpen(false)}  >
            <SystemIcons.Close />
          </IconButton>
          <Line />
          <H3>
            {t('Country & language selector')}
          </H3>
        </HeaderColumn>}

        <Column>
          <GroupHeading color={COLORS.white}>{t('Americas')}</GroupHeading>
          {['la', 'br', 'ca', 'mx', 'us'].map(renderCountry)}

          <GroupHeading color={COLORS.white}>{t('Asia / Pacific')}</GroupHeading>
          {['au', 'cn', 'hk', 'in', 'jp', 'kr', 'my', 'nz', 'sg'].map(renderCountry)}
        </Column>

        <Column>
          <GroupHeading color={COLORS.white}>{t('Europe')}</GroupHeading>
          {['be-nl', 'be-fr', 'dk', 'de', 'es', 'fr', 'it', 'nl', 'no', 'at', 'pl', 'ch', 'fi', 'se', 'gb'].map(renderCountry)}
        </Column>

        <Column>
          <GroupHeading color={COLORS.white}>{t('Location not listed?')}</GroupHeading>
          {['distributors', 'int'].map(renderCountry)}
        </Column>

      </DropdownContainer>
    </Container>
  );
}

export default LanguageSelector;